import React, { useEffect, useState } from 'react';
import { FileText, Image as ImageIcon, Video, Download, X, Bell } from 'lucide-react';

interface MediaItem {
  id: string;
  title: string;
  file_url: string;
  category: 'video' | 'document' | 'image' | 'sermon_media';
  created_at: string;
}

// Update Props to include userData
interface Props {
  isOpen: boolean;
  onClose: () => void;
  shopId: number;
  userData: any; // Add this line
}

export const MemberMediaAccess = ({ isOpen, onClose, shopId, userData }: Props) => {
  const [media, setMedia] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(true);

  // Helper function to fix TS2304: Cannot find name 'getIcon'
  const getIcon = (category: string) => {
    switch (category) {
      case 'video': return <Video className="text-red-500" />;
      case 'image': return <ImageIcon className="text-blue-500" />;
      default: return <FileText className="text-purple-500" />;
    }
  };

  const [readIds, setReadIds] = useState<string[]>(() => {
    // Uses the phone_number from the passed prop
    const saved = localStorage.getItem(`read_media_${userData?.phone_number}`);
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    if (isOpen) fetchMedia();
  }, [isOpen, userData]); // Added userData as dependency

  const fetchMedia = async () => {
    setLoading(true);
    try {
      const response = await fetch('https://n8n.tenear.com/webhook/member-media-fetch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shop_id: userData?.shop_id || shopId,
          org_id: userData?.org_id,
          role: userData?.role,
          phone_number: userData?.phone_number,
          action: 'FETCH_MEMBER_MEDIA'
        }),
      });

      const data = await response.json();
      const mediaList = Array.isArray(data) ? data : (data.media || []);
      setMedia(mediaList);
    } catch (error) {
      console.error("Failed to fetch media via n8n:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = (id: string) => {
    if (!readIds.includes(id)) {
      const updatedReadIds = [...readIds, id];
      setReadIds(updatedReadIds);
      localStorage.setItem(`read_media_${userData?.phone_number}`, JSON.stringify(updatedReadIds));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white w-full max-w-lg rounded-t-[2.5rem] sm:rounded-[2.5rem] h-[85vh] sm:h-auto sm:max-h-[80vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-gray-100 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-black text-gray-900 flex items-center gap-2">
              <Bell className="w-6 h-6 text-blue-600" /> Newsletters
            </h2>
          </div>
          <button onClick={onClose} className="p-2 bg-gray-100 rounded-full">
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {loading ? (
            <p className="text-center py-10 font-bold text-gray-400">Loading...</p>
          ) : (
            media.map((item) => (
              <div key={item.id} className={`p-4 rounded-2xl border flex items-center justify-between ${!readIds.includes(item.id) ? 'bg-white border-blue-100 shadow-sm' : 'bg-gray-50 opacity-60'}`}>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 flex items-center justify-center">{getIcon(item.category)}</div>
                  <div>
                    <h4 className="font-bold text-sm text-gray-900">{item.title}</h4>
                    <p className="text-[10px] uppercase text-gray-400 font-black">{item.category}</p>
                  </div>
                </div>
                <a 
                  href={item.file_url} 
                  target="_blank" 
                  rel="noreferrer" 
                  onClick={() => handleMarkAsRead(item.id)}
                  className="p-2 bg-blue-50 text-blue-600 rounded-lg"
                >
                  <Download size={18} />
                </a>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};
